import React, { useState, useEffect } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerClose,
  DrawerHeader,
} from "@/src/components/ui/drawer";
import { Switch } from "@/src/components/ui/switch";
import { Slider } from "@/src/components/ui/slider";
import { TABLE_SETTINGS } from "@/Poker/data/Constants";
import styles from "./TableSettings.module.css";
import useTableSettings from "@/src/hooks/data/useTableSettings";
import useApplyTableSettings from "@/src/hooks/data/useApplyTableSettings";
import { IoCloseSharp } from "react-icons/io5";
import useGameSocket from "@/src/hooks/utils/useGameSocket";
import useGameConnection from "@/src/hooks/data/useGameConnection";

const TABS = [
  { name: "Basic", key: "basic" },
  { name: "Buy-in", key: "buyin" },
  { name: "Betting Options", key: "betting" },
];

export default function TableSettings({ tempTableId, open, onOpenChange }) {
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const { tableSettings } = useTableSettings();
  const {
    updateHandStrength,
    updateAutoTableSwitch,
    updateAutoMuck,
    updateAutoRebuy,
    updateAutoBuyIn,
    updateAutoBuyInPercentage,
    updateAutoTopup,
    updateAutoTopupFallsBelow,
    updateAutoTopupAmount,
    updateEmojiToggle,
    updateThrowableToggle,
    updateCardSortingSetting,

    // Add more update functions as needed
  } = useApplyTableSettings();
  const { sendPlayerConfigProtocol } = useGameSocket(tempTableId);
  const { connected } = useGameConnection(tempTableId);

  // For the range slider in Buy-in tab
  const minValue = 10;
  const maxValue = 100;
  const fallsBelow =
    tableSettings.autoTopupConfig?.fallsBelowAmount ?? minValue;
  const topUpTo = tableSettings.autoTopupConfig?.topUpAmount ?? maxValue;
  const [range, setRange] = useState([fallsBelow, topUpTo]);

  // For Auto Buy In slider
  const autoBuyInMin = 50;
  const autoBuyInMax = 100;

  // Keep local state in sync with Redux
  useEffect(() => {
    setRange([
      tableSettings.autoTopupConfig?.fallsBelowAmount ?? minValue,
      tableSettings.autoTopupConfig?.topUpAmount ?? maxValue,
    ]);
  }, [
    tableSettings.autoTopupConfig?.fallsBelowAmount,
    tableSettings.autoTopupConfig?.topUpAmount,
  ]);

  const handleRangeChange = ([newFallsBelow, newTopUpTo]) => {
    // Ensure topUpTo is never less than fallsBelow
    if (newTopUpTo < newFallsBelow) newTopUpTo = newFallsBelow;
    setRange([newFallsBelow, newTopUpTo]);
    updateAutoTopupFallsBelow(newFallsBelow);
    updateAutoTopupAmount(newTopUpTo);
  };

  const handleAutoBuyInSlider = (value) => {
    updateAutoBuyInPercentage(value[0]);
  };

  const handleTableSettingsOpenChange = (open) => {
    if (!open) {
      sendPlayerConfigProtocol();
    }
    onOpenChange();
  };

  const {
    showHandStrength,
    autoTableSwitch,
    autoMuck,
    showEmojis,
    showThrowables,
    sortCardsByRank,
    autoRebuy,
    autoBuyIn = {},
    autoTopupConfig = {},
    bettingOptions = {},
  } = tableSettings || {};
  const {
    enabled: autoBuyInEnabled = false,
    percentage: autoBuyInPercentage = 50,
  } = autoBuyIn;

  const {
    enabled: autoTopupEnabled = false,
    fallsBelowAmount = minValue,
    topUpAmount = maxValue,
  } = autoTopupConfig;

  const { defaultPreflopPresetValue = "", defaultPostflopPresetValue = "" } =
    bettingOptions;

  return (
    <Drawer open={open && connected} onOpenChange={handleTableSettingsOpenChange}>
      <DrawerContent className={styles.container}>
        <DrawerHeader className={styles.tabsBarHeader}>
          <div className={styles.drawerHeader}>
            <DrawerClose asChild>
              <IoCloseSharp className={styles.drawerClose} />
            </DrawerClose>
            <h2 className={styles.drawerTitle}>Table Settings</h2>
          </div>
          <div className={styles.tabsBar}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={
                  activeTab === tab.key
                    ? styles.activeTabButton
                    : styles.tabButton
                }
                onClick={() => setActiveTab(tab.key)}
                type='button'
              >
                {tab.name}
              </button>
            ))}
          </div>
        </DrawerHeader>
        <div className={styles.drawerContent}>
          {activeTab === "basic" && (
            <>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Show Hand Strength</span>
                  <Switch
                    checked={showHandStrength}
                    onCheckedChange={updateHandStrength}
                  />
                </div>
                <div className={styles.settingDescription}>
                  Display the strength of your hand during play.
                </div>
              </div>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Sort Cards by Rank</span>
                  <Switch
                    checked={sortCardsByRank}
                    onCheckedChange={updateCardSortingSetting}
                  />
                </div>
                <div className={styles.settingDescription}>
                  Automatically sort your cards from lowest to highest rank for easier hand evaluation.
                </div>
              </div>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Auto Table Switch</span>
                  <Switch
                    checked={autoTableSwitch}
                    onCheckedChange={updateAutoTableSwitch}
                  />
                </div>
                <div className={styles.settingDescription}>
                  Automatically switch to a new table when your current table
                  breaks.
                </div>
              </div>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Auto Muck</span>
                  <Switch checked={autoMuck} onCheckedChange={updateAutoMuck} />
                </div>
                <div className={styles.settingDescription}>
                  Automatically muck your losing hands at showdown.
                </div>
              </div>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Emojis</span>
                  <Switch
                    checked={showEmojis}
                    onCheckedChange={updateEmojiToggle}
                  />
                </div>
                {/* <div className={styles.settingDescription}></div> */}
              </div>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Throwables</span>
                  <Switch
                    checked={showThrowables}
                    onCheckedChange={updateThrowableToggle}
                  />
                </div>
                {/* <div className={styles.settingDescription}></div> */}
              </div>
              {/* Add more basic settings as needed */}
            </>
          )}
          {activeTab === "buyin" && (
            <>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Auto Re-Buy</span>
                  <Switch
                    checked={autoRebuy}
                    onCheckedChange={updateAutoRebuy}
                  />
                </div>
                <div className={styles.settingDescription}>
                  Automatically rebuy chips if you fall below the minimum
                  buy-in.
                </div>
              </div>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Auto Buy-In</span>
                  <Switch
                    checked={autoBuyInEnabled}
                    onCheckedChange={updateAutoBuyIn}
                  />
                </div>
                <div className={styles.settingDescription}>
                  Enable to automatically buy-in with your preferred amount when
                  joining a table.
                </div>
                {autoBuyInEnabled && (
                  <>
                    <div className={styles.autoBuyInDescription}>
                      You will be seated with{" "}
                      <span className={styles.autoBuyInPercentage}>
                        {autoBuyInPercentage}%
                      </span>{" "}
                      of the table max buy-in
                    </div>
                    <div
                      style={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        width: "100%",
                        margin: "var(--gap-12) 0",
                      }}
                    >
                      <Slider
                        min={autoBuyInMin}
                        max={autoBuyInMax}
                        value={[autoBuyInPercentage]}
                        onValueChange={handleAutoBuyInSlider}
                        step={1}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Auto Top-up</span>
                  <Switch
                    checked={autoTopupEnabled}
                    onCheckedChange={updateAutoTopup}
                  />
                </div>
                <div className={styles.settingDescription}>
                  Automatically top up your stack when it falls below a set
                  threshold.
                </div>
                {autoTopupEnabled && (
                  <>
                    <span className={styles.autoTopUpDescription}>
                      If your stack drops below{" "}
                      <span className={styles.autoTopUpBelowValue}>
                        {range[0]}%
                      </span>{" "}
                      of the table max buy-in, your stack will automatically be
                      topped up to{" "}
                      <span className={styles.autoTopUpToValue}>
                        {range[1]}%
                      </span>{" "}
                      of the table max buy-in
                    </span>
                    <div
                      style={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        width: "100%",
                        marginBottom: "var(--gap-16)",
                      }}
                    >
                      <Slider
                        min={minValue}
                        max={maxValue}
                        value={range}
                        onValueChange={handleRangeChange}
                        step={1}
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {activeTab === "betting" && (
            <>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Default Preflop Preset</span>
                </div>
                <div className={styles.settingDescription}>
                  Set your default preflop bet preset value.
                </div>
                <span>{defaultPreflopPresetValue}</span>
                {/* Add a slider or select for changing this value */}
              </div>
              <div className={styles.settingSection}>
                <div className={styles.settingRow}>
                  <span>Default Postflop Preset</span>
                </div>
                <div className={styles.settingDescription}>
                  Set your default postflop bet preset value.
                </div>
                <span>{defaultPostflopPresetValue}</span>
                {/* Add a slider or select for changing this value */}
              </div>
              {/* Add more betting options as needed */}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
